import type { RouteLocationRaw } from 'vue-router'

export interface SearchHistoryTopResult {
  id: string
  title: string
  nodeType: string
  author?: string
  publishedAt?: string
  editionNumber?: string | number
  url?: RouteLocationRaw
}

export interface SearchHistoryItem {
  term: string
  topResult?: SearchHistoryTopResult
}

export type HistoryContext = 'literature' | 'law-and-commentary' | 'global'

function transformHistoryData(data: unknown): SearchHistoryItem[] {
  if (!Array.isArray(data)) {
    return []
  }

  return data.flatMap((item): SearchHistoryItem[] => {
    if (typeof item === 'string' && item.trim().length > 0) {
      return [{ term: item }]
    }
    if (typeof item === 'object' && item !== null && 'term' in item) {
      return [{
        term: item.term,
        topResult: item.topResult
          ? {
              id: item.topResult.id,
              title: item.topResult.title,
              nodeType: item.topResult.nodeType,
              author: item.topResult.author,
              publishedAt: item.topResult.publishedAt,
              editionNumber: item.topResult.editionNumber,
              url: item.topResult.url,
            }
          : undefined,
      }]
    }
    return []
  })
}

export function useSearchHistory(key: HistoryContext) {
  const historyState = new Map<HistoryContext, { personalization: ReturnType<typeof usePersonalization> }>()

  if (!historyState.has(key)) {
    const personalization = usePersonalization(`search-history-${key}`)

    historyState.set(key, { personalization })
  }

  const state = historyState.get(key)!
  const { personalization } = state

  const searchHistory = computed<SearchHistoryItem[]>({
    get: () => transformHistoryData(personalization.data.value),
    set: (value: SearchHistoryItem[]) => {
      personalization.set(value)
    },
  })

  function addSearchTermToHistory(term: string, topResult?: SearchHistoryTopResult) {
    const trimmedTerm = term.trim()
    if (trimmedTerm.length === 0) {
      return
    }

    const currentHistory = [...searchHistory.value]
    const existingIndex = currentHistory.findIndex(item => item.term.toLowerCase() === trimmedTerm.toLowerCase())

    const newItem: SearchHistoryItem = {
      term: trimmedTerm,
      topResult,
    }

    if (existingIndex !== -1) {
      currentHistory.splice(existingIndex, 1)
    }

    currentHistory.unshift(newItem)

    if ((key === 'law-and-commentary' || key === 'literature') && currentHistory.length > 3) {
      currentHistory.splice(3)
    }
    else if (currentHistory.length > 10) {
      currentHistory.splice(10)
    }

    searchHistory.value = currentHistory
  }

  function removeSearchFromHistory(term: string) {
    const currentHistory = [...searchHistory.value]
    const index = currentHistory.findIndex(item => item.term.toLowerCase() === term.toLowerCase())
    if (index !== -1) {
      currentHistory.splice(index, 1)
      searchHistory.value = currentHistory
    }
  }

  function clearSearchHistory() {
    searchHistory.value = []
  }

  return {
    searchHistory,
    addSearchTermToHistory,
    clearSearchHistory,
    removeSearchFromHistory,
  }
}
